import { useEffect, useRef } from "react";
import { CyberpassButton } from "./Input.jsx";
import { useTranslation } from "./i18n";

/**
 * @param {object} props
 * @param {string} [props.title]
 * @param {any} props.content
 * @param {boolean} props.showModal
 * @param {(_: boolean) => void} props.setShowModal
 * @param {(_: boolean) => void} [props.setModalConfirmed]
 * @param {boolean} [props.hideConfirmButton]
 * @param {string} [props.confirmButtonText]
 * @param {string | undefined} [props.className]
 * @param {"center" | "right"} [props.align]
 * @param {() => void} [props.onConfirm]
 * @param {Array<React.DOMElement>} [props.additionalButtons]
 * @param {"default" | "alert"} [props.style]
 * @param {"left" | "center"} [props.titleAlign]
 * @param {boolean} [props.disableConfirmBtn]
 * @returns {React.Component}
 */
export default function Modal({
  title,
  content,
  showModal,
  setShowModal,
  setModalConfirmed,
  hideConfirmButton,
  confirmButtonText,
  align,
  className,
  onConfirm,
  additionalButtons,
  style,
  titleAlign,
  disableConfirmBtn = false,
}) {
  const { t } = useTranslation();
  if (confirmButtonText == null) {
    confirmButtonText = t("common.confirm");
  }

  if (titleAlign == null) {
    titleAlign = "left";
  }

  /**
   * @type {React.MutableRefObject<HTMLDivElement|null>}
   */
  const modalRef = useRef(null);

  const closeModal = () => {
    setShowModal(false);
  };

  /**
   * @param {Event} event
   */
  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      closeModal();
    }
  };

  useEffect(() => {
    /**
     * @param {KeyboardEvent} event - The key down event.
     */
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModal]);

  let outerClassNames, innerClassNames;
  if (!align || align === "center") {
    outerClassNames = "flex justify-center items-center";

    innerClassNames = "max-w-[70vh] my-6 ";
  } else if (align === "right") {
    outerClassNames = "";
    innerClassNames = "ml-auto sm:max-w-1/2 md:max-w-2xl max-h-screen";
  } else {
    throw `Wrong align type: ${align}`;
  }

  if (style === "alert") {
    innerClassNames += " border border-red-500 border-2";
  }

  return (
    <>
      {showModal ? (
        <>
          <div
            onMouseDown={handleClickOutside}
            className={`${outerClassNames} overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none backdrop-blur-md bg-gray-30`}
          >
            {/*content*/}
            <div
              className={`${innerClassNames} border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-c-gray-600 outline-none focus:outline-none ${className}`}
              ref={modalRef}
            >
              {/*header*/}
              {title ? (
                <h1
                  className={`text-2xl p-3 font-semibold ${titleAlign == "center" ? "text-center" : ""}`}
                >
                  {title}
                </h1>
              ) : null}
              {/*body*/}
              <div className="overflow-y-auto">
                <div className="p-3">{content}</div>
                {/*footer*/}
                {hideConfirmButton ? null : (
                  <div className="flex items-center justify-between p-3 rounded-b">
                    <CyberpassButton
                      type="button"
                      disabled={disableConfirmBtn}
                      onClick={() => {
                        if (setModalConfirmed != null) {
                          setModalConfirmed(true);
                        }
                        if (onConfirm != null) {
                          onConfirm();
                        }
                        closeModal();
                      }}
                    >
                      {confirmButtonText}
                    </CyberpassButton>
                    {additionalButtons}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </>
  );
}
